import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Play, Pause } from 'lucide-react';
import { useContent } from '../../lib/content';
import { pickLocale } from '../../lib/api';

/**
 * Reel — horizontal video marquee with parallax & hover-pause.
 *
 * Renders the 7 MODARIA-generated 5-second clips (V01, V03, V04, V08, V10,
 * V11, V12) as an infinite scrolling row of vertical 9:16 video tiles,
 * each lazily auto-playing when entering the viewport.
 *
 * Subliminal beat between Manifesto/Showcase and the technical sections:
 * proves MODARIA also moves (literally) — image AND video in one place.
 */

const VIDEO_IDS = ['V01', 'V03', 'V04', 'V08', 'V10', 'V11', 'V12'];
const SPEED_BASE_PX_PER_SEC = 38;

const VIDEO_CAPTIONS = {
  V01: { es: 'Editorial · cuero',     en: 'Editorial · leather' },
  V03: { es: 'Editorial · capa',      en: 'Editorial · cape' },
  V04: { es: 'Studio · punto',        en: 'Studio · knit' },
  V08: { es: 'Studio · outerwear',    en: 'Studio · outerwear' },
  V10: { es: 'Studio · workwear',     en: 'Studio · workwear' },
  V11: { es: 'Editorial · resort',    en: 'Editorial · resort' },
  V12: { es: 'Studio · jacquard',     en: 'Studio · jacquard' },
};

function VideoTile({ id, locale, paused }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !paused) v.play().catch(() => {});
        else v.pause();
      });
    }, { threshold: 0.25 });
    obs.observe(v);
    return () => obs.disconnect();
  }, [paused]);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (paused) v.pause();
    else v.play().catch(() => {});
  }, [paused]);

  const cap = VIDEO_CAPTIONS[id]?.[locale] || id;
  const src = `/uploads/looks/video/${id}.mp4`;
  const poster = `/uploads/looks/video/${id}-poster.webp`;

  return (
    <div
      className="relative shrink-0 w-[220px] md:w-[280px] aspect-[9/16] overflow-hidden border border-line bg-bg-secondary group"
      data-testid={`reel-tile-${id}`}
    >
      <video
        ref={ref}
        src={src}
        poster={poster}
        muted
        loop
        playsInline
        preload="none"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-x-0 bottom-0 px-3 py-2 bg-gradient-to-t from-bg/80 to-transparent">
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink">{id}</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-acid truncate max-w-[140px]">
            {cap}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function Reel() {
  const { content, locale } = useContent();
  const data = content.reel || {};
  const sectionRef = useRef(null);
  const [paused, setPaused] = useState(false);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  // Parallax: row-A slides leftwards faster than row-B (different perceived depth)
  const xA = useTransform(scrollYProgress, [0, 1], ['0%', '-22%']);
  const xB = useTransform(scrollYProgress, [0, 1], ['0%', '12%']);

  const t = (k, fb = '') => pickLocale(data[k], locale, fb);
  const clips = (data.video_ids?.length ? data.video_ids : VIDEO_IDS);
  // Build a long row by triplicating for seamless marquee.
  const rowA = [...clips, ...clips, ...clips];
  const rowB = [...clips.slice().reverse(), ...clips.slice().reverse(), ...clips.slice().reverse()];

  return (
    <section
      ref={sectionRef}
      id="reel"
      data-testid="reel-section"
      className="relative section bg-bg overflow-hidden"
    >
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10 md:mb-14">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-acid animate-pulse" />
              <span className="text-eyebrow text-acid">{t('eyebrow', 'REEL · MODARIA EN MOVIMIENTO')}</span>
            </div>
            <h2
              data-testid="reel-title"
              className="font-display text-4xl md:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[0.98] text-ink"
            >
              {t('title', 'Imagen, vídeo y manifiesto. La misma calidad.')}
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-6 flex flex-col items-start lg:items-end gap-4">
            <p className="text-ink-muted text-base md:text-lg leading-relaxed text-left lg:text-right">
              {t('lead', 'Cada clip de 5 segundos sale del mismo proceso que tus páginas de producto — con su motor de IA registrado, su aprobación humana firmada y su huella de carbono medida.')}
            </p>
            <button
              type="button"
              onClick={() => setPaused((p) => !p)}
              data-testid="reel-pause-toggle"
              className="inline-flex items-center gap-2 px-3 py-2 border border-line text-ink hover:border-acid hover:text-acid transition-colors font-mono text-[10px] uppercase tracking-[0.22em]"
              aria-pressed={paused}
            >
              {paused ? <Play size={11} /> : <Pause size={11} />}
              {paused ? (locale === 'es' ? 'REPRODUCIR' : 'PLAY ALL') : (locale === 'es' ? 'PAUSAR' : 'PAUSE ALL')}
            </button>
          </div>
        </div>
      </div>

      {/* Row A — leftward marquee with scroll parallax */}
      <div className="overflow-hidden mb-3">
        <motion.div
          style={{ x: xA }}
          className="flex gap-3 animate-marquee-left"
          data-testid="reel-row-a"
        >
          {rowA.map((id, i) => (
            <VideoTile key={`a-${i}-${id}`} id={id} locale={locale} paused={paused} />
          ))}
        </motion.div>
      </div>

      {/* Row B — rightward marquee, slower, opposite parallax */}
      <div className="overflow-hidden">
        <motion.div
          style={{ x: xB }}
          className="flex gap-3 animate-marquee-right"
          data-testid="reel-row-b"
        >
          {rowB.map((id, i) => (
            <VideoTile key={`b-${i}-${id}`} id={id} locale={locale} paused={paused} />
          ))}
        </motion.div>
      </div>

      <div className="container-x mt-8">
        <p className="text-ink-dim text-eyebrow">
          {t('caveat', 'CLIPS DEMO · GENERADOS POR EL PIPELINE MODARIA · TU INSTANCIA ON-PREM EMITIRÁ LOS TUYOS CON TU IA REGISTRADA, VALIDACIÓN HUMANA Y HUELLA AUDITADA')}
        </p>
      </div>
    </section>
  );
}
